import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  Signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import 'jodit/esm/plugins/all';
import type { IJodit } from 'jodit/types/types';
import { HxBaseControl } from '../../base/hx-base-control';
import { buildJoditConfig } from './config/jodit-config-builder';
import type { JoditCustomButtonMap, JoditToolbarConfig } from './config/jodit-toolbar.types';
import { DEFAULT_JODIT_CONFIG } from './config/jodit.config';
import {
  applyBodyStyleToIframe,
  applyIframeStyleToDocument,
  BODY_STYLE_TAG_ID,
  extractBodyStyle,
  IFRAME_STYLE_TAG_ID,
} from './plugins/body-style.plugin';
import { JoditEditorLifecycleService, JoditEditorStateService, JoditEditorValidatorService } from './services';

/**
 * HxJoditEditor - Rich Text Editor Component
 *
 * Angular 20 component wrapping JODIT with variable insertion, form integration, full HTML document preservation, and
 * base64 image encoding (ADR-006 compliant).
 */
@Component({
  selector: 'hx-jodit-editor',
  standalone: true,
  templateUrl: './jodit-editor.html',
  styleUrl: './jodit-editor.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: HxJoditEditor,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: HxJoditEditor,
      multi: true,
    },
    JoditEditorStateService,
    JoditEditorLifecycleService,
    JoditEditorValidatorService,
  ],
  host: {
    '[class.hx-jodit-editor]': 'true',
    '[class.hx-jodit-editor--disabled]': 'disabledState()',
    '[class.hx-jodit-editor--readonly]': 'readonly()',
    '[class.hx-jodit-editor--invalid]': 'state.isInvalid()',
  },
})
export class HxJoditEditor extends HxBaseControl implements OnInit, OnDestroy, Validator {
  private readonly _platformId = inject(PLATFORM_ID);
  protected readonly _destroyRef = inject(DestroyRef);
  protected readonly state = inject(JoditEditorStateService);
  private readonly lifecycle = inject(JoditEditorLifecycleService);
  private readonly validatorService = inject(JoditEditorValidatorService);
  private readonly _editorContainer = viewChild.required<ElementRef<HTMLTextAreaElement>>('editorContainer');

  /** JODIT configuration object (merged with DEFAULT_JODIT_CONFIG) */
  readonly config = input<Partial<IJodit['options']>>({});
  /** Placeholder text when editor is empty */
  readonly placeholder = input<string>('');
  /** Toolbar configuration */
  readonly toolbar = input<JoditToolbarConfig>(true);

  /** Custom toolbar buttons */
  readonly customButtons = input<JoditCustomButtonMap>({});
  /** Maximum character length */
  readonly maxLength = input<number | undefined>(undefined);
  /** Minimum character length */
  readonly minLength = input<number | undefined>(undefined);
  /** Debounce time for value changes (milliseconds) */
  readonly debounceMs = input<number>(300);
  /** Enable HTML sanitization */
  readonly sanitize = input<boolean>(true);
  /** Height of the editor (CSS value) */
  readonly height = input<number | string>(400);

  /** Emitted when editor value changes (debounced) */
  readonly valueChange = output<string>();
  /** Emitted when editor is created and ready */
  readonly editorCreated = output<IJodit>();
  /** Emitted on content change with both HTML and text */
  readonly contentChanged = output<{ html: string; text: string }>();
  /** Emitted when editor gains focus */
  readonly focusEvent = output<void>();
  /** Emitted when editor loses focus */
  readonly blurEvent = output<void>();

  /** Current editor instance */
  readonly editor: Signal<IJodit | null> = this.state.editor;
  /** Whether editor is initialized and ready */
  readonly editorReady: Signal<boolean> = this.state.editorReady;
  /** Current character count */
  readonly characterCount: Signal<number> = this.state.characterCount;

  /** Computed: Is character count near maximum? */
  readonly isNearLimit = computed(() => {
    const max = this.maxLength();
    const count = this.characterCount();
    return max ? count / max > 0.9 : false;
  });

  /** Computed: Is character count over maximum? */
  readonly isOverLimit = computed(() => {
    const max = this.maxLength();
    const count = this.characterCount();
    return max ? count > max : false;
  });

  /** Last extracted `<body style="...">` CSS string; updated whenever content is set. */
  private _bodyStyle = '';

  /**
   * Set before a programmatic `editor.value` assignment (writeValue/initial load); consumed (and cleared) by the next
   * `_onEditorChange` so that Jodit's resulting `change` event isn't mistaken for a real user edit and doesn't mark the
   * form control dirty. Self-clearing rather than timer-based since Jodit's change event is driven by an internal
   * MutationObserver debounce with no fixed delay we can rely on.
   */
  private _suppressProgrammaticChange = false;

  /**
   * Re-inject the `iframeStyle` config CSS into the iframe head. In `editHTMLDocumentMode`, `document.write()` wipes
   * Jodit-injected styles whenever content is set. Calling this after each value assignment restores baseline WYSIWYG
   * styles (body margins, table layout, image constraints) even for empty content.
   */
  private _reapplyIframeStyle(editor: IJodit): void {
    const config = this._effectiveConfig();
    if (config.editHTMLDocumentMode) {
      applyIframeStyleToDocument(editor, (config.iframeStyle as string) || '');
    }
  }

  /** Computed: Effective JODIT configuration */
  private readonly _effectiveConfig = computed<IJodit['options']>(() =>
    buildJoditConfig(DEFAULT_JODIT_CONFIG, {
      userConfig: this.config(),
      placeholder: this.placeholder(),
      readonly: this.readonly(),
      disabled: this.disabledState(),
      height: this.height(),
      toolbar: this.toolbar(),
      customButtons: this.customButtons(),
    })
  );

  constructor() {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    effect(() => {
      const editor = this.state.editor();
      const disabled = this.disabledState();
      if (editor && this.state.editorReady()) {
        editor.setReadOnly(disabled ?? false);
      }
    });

    effect(() => {
      const editor = this.state.editor();
      const readonly = this.readonly();
      if (editor && this.state.editorReady()) {
        editor.setReadOnly(readonly);
      }
    });

    if (isPlatformBrowser(this._platformId)) {
      afterNextRender(() => void this._initializeEditor());
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.state.setupContentChangeObserver(this.debounceMs(), () => this._emitValueChange());
  }
  ngOnDestroy(): void {
    this.lifecycle.destroyEditor();
  }

  private async _initializeEditor(): Promise<void> {
    const container = this._editorContainer()?.nativeElement;
    if (!container) {
      console.error('[HxJoditEditor] Container element not found');
      return;
    }

    await this.lifecycle.initializeEditor(container, this._effectiveConfig(), {
      onChange: (newValue: string) => this._onEditorChange(newValue),
      onFocus: () => this._onEditorFocus(),
      onBlur: () => this._onEditorBlur(),
      onReady: (editorInstance: IJodit) => this._onEditorReady(editorInstance),
      onAfterSetMode: () => {
        const ed = this.state.editor();
        if (ed) {
          applyBodyStyleToIframe(ed, this._bodyStyle);
          this._reapplyIframeStyle(ed);
        }
      },
      onAfterPaste: () => {
        const ed = this.state.editor();
        if (ed) {
          applyBodyStyleToIframe(ed, this._bodyStyle);
          this._reapplyIframeStyle(ed);
        }
      },
    });
  }

  private _onEditorReady(editorInstance: IJodit): void {
    const initialValue = this.state.internalValue();
    if (initialValue) {
      this._bodyStyle = extractBodyStyle(initialValue);
      this._suppressProgrammaticChange = true;
      editorInstance.value = initialValue;
    }
    applyBodyStyleToIframe(editorInstance, this._bodyStyle);
    this._reapplyIframeStyle(editorInstance);
    this.state.setEditorReady(true);
    this.editorCreated.emit(editorInstance);
    this.state.updateCharacterCountFromEditor(editorInstance);
  }

  private _onEditorChange(newValue: string): void {
    const editor = this.state.editor();
    if (!editor) {
      return;
    }
    this.state.setInternalValue(newValue);
    this.state.updateCharacterCountFromEditor(editor);
    if (this._suppressProgrammaticChange) {
      // One-shot: consume exactly the echo of the assignment we just made, whenever Jodit's
      // MutationObserver-debounced change event actually fires - no fixed timing window to guess.
      this._suppressProgrammaticChange = false;
      return;
    }
    this.state.notifyContentChange();
  }

  private _onEditorFocus(): void {
    this.focusEvent.emit();
  }
  private _onEditorBlur(): void {
    this.markTouched();
    this.blurEvent.emit();
  }

  private _emitValueChange(): void {
    const editor = this.state.editor();
    if (!editor) {
      return;
    }
    // Temporarily remove injected style tags so they do not leak into emitted HTML.
    const iDoc = editor.editorDocument;
    iDoc?.getElementById(BODY_STYLE_TAG_ID)?.remove();
    iDoc?.getElementById(IFRAME_STYLE_TAG_ID)?.remove();

    // With editHTMLDocumentMode: true, editor.value returns the full document natively.
    const html = editor.value;
    const text = editor.text;

    // Re-inject so the user's visual view remains consistent.
    applyBodyStyleToIframe(editor, this._bodyStyle);
    this._reapplyIframeStyle(editor);

    this.notifyChange(html);
    this.valueChange.emit(html);
    this.contentChanged.emit({ html, text });
  }

  protected override _setValue(value: string): void {
    const content = value || '';
    this._bodyStyle = extractBodyStyle(content);
    this.state.setInternalValue(content);
    const editor = this.state.editor();
    if (editor && this.state.editorReady()) {
      if (editor.value !== content) {
        this._suppressProgrammaticChange = true;
        editor.value = content;
        applyBodyStyleToIframe(editor, this._bodyStyle);
        this._reapplyIframeStyle(editor);
        this.state.updateCharacterCountFromEditor(editor);
      }
    }
  }

  protected override _validate(control: AbstractControl): ValidationErrors | null {
    return this.validatorService.validate(control, {
      required: this.required(),
      minLength: this.minLength(),
      maxLength: this.maxLength(),
    });
  }

  /** Get current HTML content (without the injected body-style helper tag) */
  getValue(): string {
    const editor = this.state.editor();
    if (!editor) {
      return '';
    }

    const iDoc = editor.editorDocument;
    iDoc?.getElementById(BODY_STYLE_TAG_ID)?.remove();
    iDoc?.getElementById(IFRAME_STYLE_TAG_ID)?.remove();

    const html = editor.value;

    // Re-inject so the user's visual view remains consistent.
    applyBodyStyleToIframe(editor, this._bodyStyle);
    this._reapplyIframeStyle(editor);

    return html;
  }

  /** Set HTML content */
  setValue(value: string): void {
    this._setValue(value);
  }

  /** Get plain text content (no HTML) */
  getText(): string {
    return this.state.editor()?.text || '';
  }

  /** Insert content at cursor position */
  insertContent(html: string): void {
    this.state.editor()?.s.insertHTML(html);
  }

  /** Insert a variable token at cursor position */
  insertVariable(variableId: string): void {
    const editor = this.state.editor();
    if (editor && this.state.editorReady()) {
      if (!editor.s.isFocused()) {
        editor.s.focus();
      }
      editor.s.insertHTML(variableId);
    }
  }

  /** Focus the editor */
  focus(): void {
    this.state.editor()?.s.focus();
  }

  /** Clear editor content */
  clear(): void {
    this.setValue('');
  }
}
